import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import SimpleBar from "simplebar-react";
import "simplebar-react/dist/simplebar.min.css";
import { apiClient } from "../../api/client";
import { getCharacterPortraitSrc } from "../../utils/helpers";

const AdminCharacterPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const id = searchParams.get("id");
    const apiUrl = process.env.REACT_APP_API_URL;
    const [loading, setLoading] = useState(true);
    const [characterInfo, setCharacterInfo] = useState({});
    const [situation1, setSituation1] = useState({});
    const [situation2, setSituation2] = useState({});
    const [comment, setComment] = useState("");

    useEffect(() => {
        if (!id) {
            setLoading(false);
            return;
        }

        const fetchCharacter = async () => {
            try {
                const result = await apiClient.post(
                    `${apiUrl}api/acp/get-character-info`,
                    { character_id: id },
                );
                if (result) setCharacterInfo(result);
            } catch (err) {
                console.error(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchCharacter();
    }, [apiUrl, id]);

    useEffect(() => {
        if (!characterInfo?.idSituation1 || !characterInfo?.idSituation2) {
            return;
        }

        const fetchSituation = async (situationId, setter) => {
            try {
                const result = await apiClient.post(
                    `${apiUrl}api/ucp/get-role-situation`,
                    { situation_id: situationId },
                );
                setter(result || {});
            } catch (err) {
                console.error(err.message);
            }
        };

        fetchSituation(characterInfo.idSituation1, setSituation1);
        fetchSituation(characterInfo.idSituation2, setSituation2);
    }, [apiUrl, characterInfo]);

    const handleSubmitForm = async (event) => {
        event.preventDefault();

        const formData = new FormData(event.target);
        const payload = Object.fromEntries(formData.entries());
        const action = event.nativeEvent.submitter.name;

        try {
            await apiClient.post(`${apiUrl}api/acp/change_application_status`, {
                character_id: payload.character_id,
                admin_comment: payload.admin_comment,
                admin_action: action,
            });
            window.location.href = "/acp/";
        } catch (err) {
            console.error("Ошибка соединения:", err);
        }
    };

    if (!id) {
        return (
            <div className="admin-page admin-page--message">
                Не указан ID персонажа.
            </div>
        );
    }

    if (loading) return <div className="admin-page">Загрузка...</div>;
    if (!characterInfo?.name) {
        return (
            <div className="admin-page admin-page--message">
                Не удалось получить информацию о персонаже.
            </div>
        );
    }

    const raceMap = {
        1: ["human", "ЧЕЛОВЕК"],
        2: ["elf", "ЭЛЬФ"],
        4: ["dwarf", "КРАСНОЛЮД"],
        6: ["nimf", "НИМФА"],
    };
    const [, raceTitle] = raceMap[characterInfo.gender] || ["human", "ЧЕЛОВЕК"];

    const portraitSrc = getCharacterPortraitSrc(characterInfo);

    const prompt1 =
        characterInfo.situationPrompt1 || situation1?.content || "";
    const prompt2 =
        characterInfo.situationPrompt2 || situation2?.content || "";

    return (
        <div className="admin-page admin-application">
            <div className="ucp-two-blocks-container ucp-two-blocks-container-acp admin-character-layout">
                <div>
                    <button
                        type="button"
                        className="ucp-simple-blur-button ucp-button-back"
                        onClick={() => navigate("/acp/check-characters")}
                    >
                        НАЗАД
                    </button>
                    <div className="character-body">
                        <img
                            src={portraitSrc}
                            alt=""
                            onError={(e) => {
                                const sex =
                                    characterInfo.sex === 0 ? "male" : "female";
                                const skin = characterInfo.selectedSkin ?? 2;
                                const human = `/media/human_${sex}_${skin}.png`;
                                if (!e.currentTarget.dataset.triedHuman) {
                                    e.currentTarget.dataset.triedHuman = "1";
                                    e.currentTarget.src = human;
                                    return;
                                }
                                e.currentTarget.src =
                                    "/media/shield_kadia_02.png";
                                e.currentTarget.onerror = null;
                            }}
                        />
                        <span>
                            {characterInfo.name
                                ?.replaceAll("_", " ")
                                .toUpperCase()}
                        </span>
                    </div>
                </div>
                <SimpleBar className="ucp-second-container admin-character-simplebar">
                    <h1>ID ПЕРСОНАЖА</h1>
                    <div className="ucp-simple-info-block ucp-simple-info-block--yellow-shadow">
                        {characterInfo.id}
                    </div>
                    <h1>ПОЛ</h1>
                    <div className="ucp-simple-info-block ucp-simple-info-block--yellow-shadow">
                        {characterInfo.sex === 0 ? "МУЖЧИНА" : "ЖЕНЩИНА"}
                    </div>
                    <h1>РАСА</h1>
                    <div className="ucp-simple-info-block ucp-simple-info-block--yellow-shadow">
                        {raceTitle}
                    </div>
                    <h1>КВЕНТА ПЕРСОНАЖА</h1>
                    <div className="ucp-simple-info-block ucp-simple-info-block--yellow-shadow">
                        {characterInfo.prehistory}
                    </div>
                    <h1>ЧЕРТЫ ХАРАКТЕРА</h1>
                    <div className="ucp-simple-info-block ucp-simple-info-block--yellow-shadow">
                        {characterInfo.traits}
                    </div>
                    <h1>НАВЫКИ, УВЛЕЧЕНИЯ</h1>
                    <div className="ucp-simple-info-block ucp-simple-info-block--yellow-shadow">
                        {characterInfo.skills}
                    </div>
                    <h1>ВНЕШНИЕ ПРИМЕТЫ</h1>
                    <div className="ucp-simple-info-block ucp-simple-info-block--yellow-shadow">
                        {characterInfo.appearance}
                    </div>
                    <h1>РОЛЕВЫЕ СИТУАЦИИ</h1>
                    <div className="ucp-simple-info-block">{prompt1}</div>
                    <div className="ucp-simple-info-block ucp-simple-info-block--yellow-shadow">
                        {characterInfo.answerSituation1}
                    </div>
                    <div className="ucp-simple-info-block">{prompt2}</div>
                    <div className="ucp-simple-info-block ucp-simple-info-block--yellow-shadow">
                        {characterInfo.answerSituation2}
                    </div>
                    <form onSubmit={handleSubmitForm}>
                        <textarea
                            name="admin_comment"
                            className="ucp-simple-input-text"
                            placeholder="Ответ по заявке..."
                            maxLength={500}
                            value={comment}
                            onChange={(e) =>
                                setComment(
                                    e.target.value.replace(/[&%#@*<>]/g, ""),
                                )
                            }
                            style={{ minHeight: "180px" }}
                        />
                        <input
                            type="hidden"
                            name="character_id"
                            value={characterInfo.id}
                        />
                        <div className="admin-character-actions">
                            <button
                                type="submit"
                                name="accept"
                                className="ucp-simple-yellow-button buttons-accept"
                            >
                                ОДОБРИТЬ
                            </button>
                            <button
                                type="submit"
                                name="reject"
                                className="ucp-simple-yellow-button buttons-accept white-background"
                            >
                                ОТКЛОНИТЬ
                            </button>
                        </div>
                    </form>
                </SimpleBar>
            </div>
        </div>
    );
};

export default AdminCharacterPage;
